import { ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { Parse } from 'src/boot/parse'
import * as svc from '../services/appUpdate'

/**
 * Store updates for the phone apps — built to HELP, not to nag (owner, 2026-10-29: "make it
 * friendly so users benefit and don't get angry at us for updating often").
 *
 * Two different things, on purpose:
 *
 *  · REQUIRED — only when this app is older than `planner_app_min_version` (Parse.Config,
 *    set in the CRM's Settings page, 0 = never). The operator raises it only when an old app
 *    would really break. A blocking screen; on Android, Google's own full-screen update.
 *
 *  · AVAILABLE — a newer version is in the store. A small card, never a modal, and only:
 *      – once the version has been out SOFT_AFTER_DAYS (no nag on release day, and a bad
 *        release can be pulled before anyone is asked),
 *      – after the board has settled (SOFT_DELAY_MS), never over the splash or login work,
 *      – once per version per SNOOZE_DAYS — "Later" really means later.
 *    On Android "Update" downloads in the BACKGROUND while they keep working; a second small
 *    card offers the restart when it is ready. On iPhone it opens the App Store.
 *
 * ⚠️ State is module-level (one instance app-wide). Until 2026-10-29 every caller built its
 * own refs, so App.vue found the update and AppUpdateDialog — holding different refs —
 * never showed it.
 *
 * Testing: `?appupdate=soft` / `?appupdate=force` / `?appupdate=ready` shows that card on
 * any platform, the web included, without asking a store or writing the snooze.
 */
const SOFT_AFTER_DAYS = 3
const SOFT_DELAY_MS = 20_000
const SNOOZE_DAYS = 7
const RECHECK_MS = 6 * 3600_000
const SNOOZE_KEY = 'dd_app_update_snooze'

const showSoft = ref(false)
const showForce = ref(false)
const showReady = ref(false)
const downloading = ref(false)
let availableVersion = ''
let lastCheck = 0
let softTimer: ReturnType<typeof setTimeout> | undefined

function snoozed(version: string): boolean {
  try {
    const s = JSON.parse(localStorage.getItem(SNOOZE_KEY) || 'null')
    return !!s && s.version === version && Date.now() < s.until
  } catch {
    return false
  }
}

async function minVersion(): Promise<number> {
  try {
    const n = Number((await Parse.Config.get()).get('planner_app_min_version'))
    return Number.isFinite(n) ? n : 0
  } catch {
    // Offline or the server is slow: never block someone on a failed read.
    return 0
  }
}

async function check(preview?: string) {
  if (preview === 'soft' || preview === 'force' || preview === 'ready') {
    showSoft.value = preview === 'soft'
    showForce.value = preview === 'force'
    showReady.value = preview === 'ready'
    return
  }
  if (!Capacitor.isNativePlatform() || process.env.NODE_ENV !== 'production') return
  lastCheck = Date.now()

  const [current, min, info] = await Promise.all([svc.currentAppVersion(), minVersion(), svc.storeInfo()])
  // Unknown version → never block.
  if (current > 0 && min > 0 && current < min) {
    showSoft.value = false
    showForce.value = true
    return
  }
  showForce.value = false

  if (svc.updateDownloaded(info)) {
    showReady.value = true
    return
  }
  if (!svc.updateAvailable(info) || svc.daysAvailable(info) < SOFT_AFTER_DAYS) return
  availableVersion = String(info?.availableVersionCode || info?.availableVersionName || '')
  if (!availableVersion || snoozed(availableVersion) || showSoft.value || softTimer) return
  softTimer = setTimeout(() => {
    softTimer = undefined
    if (!showForce.value && !showReady.value && !downloading.value) showSoft.value = true
  }, SOFT_DELAY_MS)
}

/** Back from the background: re-check now and then, so a raised minimum reaches open apps. */
function onForeground() {
  if (document.visibilityState === 'visible' && Date.now() - lastCheck > RECHECK_MS) {
    void check().catch((err) => console.warn('[appUpdate] check skipped:', err))
  }
}
let listening = false

export function useAppUpdate() {
  const checkForUpdates = async (preview?: string) => {
    if (!listening) {
      listening = true
      document.addEventListener('visibilitychange', onForeground)
    }
    await check(preview)
  }

  function later() {
    showSoft.value = false
    if (!availableVersion) return
    try {
      localStorage.setItem(SNOOZE_KEY, JSON.stringify({ version: availableVersion, until: Date.now() + SNOOZE_DAYS * 86400000 }))
    } catch { /* storage unavailable: asked again next launch */ }
  }

  async function updateNow() {
    showSoft.value = false
    const started = await svc.flexibleUpdate(() => {
      downloading.value = false
      showReady.value = true
    })
    if (started) downloading.value = true
    else await svc.openStore()
  }

  async function restartNow() {
    showReady.value = false
    await svc.finishUpdate()
  }

  const updateRequired = () => svc.immediateUpdate()

  return { showSoft, showForce, showReady, downloading, checkForUpdates, later, updateNow, restartNow, updateRequired }
}
